import React from 'react';
import { AlertTriangle, RefreshCw, Home, Mail } from 'lucide-react';
import { Button } from './ui/button';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log error to console for debugging
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    // Here you could also send the error to an error reporting service
    // Example: sendToErrorReportingService(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-sand-warm to-white flex items-center justify-center p-6">
          <div className="max-w-lg w-full text-center">
            {/* Error Icon */}
            <div className="mb-8">
              <div className="w-24 h-24 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-12 h-12 text-red-500" />
              </div>
            </div>

            {/* Error Message */}
            <h1 className="text-3xl font-playfair font-medium text-navy-deep mb-4">
              Algo salió mal
            </h1>
            <p className="text-muted-foreground mb-8 text-lg">
              Lo sentimos, ha ocurrido un error inesperado. Nuestro equipo ha sido notificado.
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={this.handleReload}
                className="bg-gold-metallic hover:bg-gold-metallic/90 text-white px-6 py-3 rounded-sm flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Recargar página
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="border-navy-deep text-navy-deep hover:bg-navy-deep hover:text-white px-6 py-3 rounded-sm flex items-center justify-center gap-2"
              >
                <Home className="w-5 h-5" />
                Ir al inicio
              </Button>
            </div>

            {/* Contact Support */}
            <div className="p-4 bg-white/50 rounded-lg border border-gray-200">
              <p className="text-sm text-muted-foreground mb-2">
                ¿El problema persiste?
              </p>
              <a 
                href="mailto:support@villapilot.ai"
                className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
              >
                <Mail className="w-4 h-4" />
                Contactar soporte
              </a>
            </div>

            {/* Error Details (only in development) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mt-8 p-4 bg-gray-100 rounded-lg text-left overflow-auto">
                <p className="text-xs font-mono text-red-600 mb-2">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
